import { hexDistance, hexNeighbors } from "../models/hex/hexGrid.ts";
import { tileKey, type TileData, type TileKey, type WorldState } from "../models/worldState.ts";

export interface MapTileView {
  readonly key: TileKey;
  readonly q: number;
  readonly r: number;
  readonly terrain: TileData["terrain"];
  readonly visibility: TileData["visibility"];
  readonly isStartingTile: boolean;
  readonly isClanTile: boolean;
  readonly isSelectable: boolean;
  readonly foodDepletion: number;
  /** Engine hex distance from the clan tile; null when the clan is not placed. */
  readonly distanceFromClan: number | null;
}

function clanIsPlaced(state: WorldState): boolean {
  return (
    state.phase === "settling" ||
    state.phase === "playing" ||
    state.phase === "victory" ||
    state.phase === "defeat"
  );
}

/** Start tiles during selectingStart, or adjacent move targets while playing. */
export function selectSelectableTiles(state: WorldState): readonly TileKey[] {
  if (state.phase === "selectingStart") {
    return Object.keys(state.tiles) as TileKey[];
  }

  if (state.phase === "playing" && !state.clan.movedThisTurn) {
    const { q, r } = state.clan.currentTile;
    return hexNeighbors(q, r).map(({ q: nq, r: nr }) => tileKey(nq, nr));
  }

  return [];
}

export function selectMapTiles(state: WorldState): readonly MapTileView[] {
  const placed = clanIsPlaced(state);
  const clanCoords = state.clan.currentTile;
  const clanKey = tileKey(clanCoords.q, clanCoords.r);
  const selectable = new Set(selectSelectableTiles(state));

  return Object.values(state.tiles).map((tile) => {
    const key = tileKey(tile.q, tile.r);
    return {
      key,
      q: tile.q,
      r: tile.r,
      terrain: tile.terrain,
      visibility: tile.visibility,
      isStartingTile: tile.isStartingTile,
      isClanTile: placed && key === clanKey,
      isSelectable: selectable.has(key),
      foodDepletion: tile.foodDepletion,
      distanceFromClan: placed
        ? hexDistance({ q: tile.q, r: tile.r }, clanCoords)
        : null,
    };
  });
}
